/**
 * CobranzaPage (F84) — panel operativo del cobranzas:
 *
 *   • Filtro de fechas: hoy / semana / mes / custom
 *   • Recaudación por CAJA arriba (users con rol=cajero)
 *   • Recaudación por ADMIN abajo (otros roles que recibieron pagos)
 *   • Total general
 *   • Sección "Contratos próximos a vencer" (próximos N días, default 5)
 *
 * Filas expandibles: click → muestra los pagos individuales del usuario.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlarmClock, ChevronDown, ChevronRight, Wallet, AlertTriangle, Calendar, Users,
  CalendarClock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/EmptyState";
import { formatMXN, formatDateTime } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { useReanclarCiclo, useDescartarRevisionCiclo } from "@/features/contratos/api";
// rc139 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileList, MobileMeta } from "@/components/ui/mobile-list";

import { useCiclosRevision, useProximosVencer, useRecaudacion } from "./api";
import type { CicloRevisionItem } from "./api";
import type { RecaudacionUserRow } from "./schemas";

type RangoPreset = "hoy" | "semana" | "mes" | "custom";

function rangoFechas(preset: RangoPreset, customDesde?: string, customHasta?: string): { desde: string; hasta: string } {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  if (preset === "hoy") return { desde: fmt(today), hasta: fmt(today) };
  if (preset === "semana") {
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    return { desde: fmt(start), hasta: fmt(today) };
  }
  if (preset === "mes") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { desde: fmt(start), hasta: fmt(today) };
  }
  return { desde: customDesde ?? fmt(today), hasta: customHasta ?? fmt(today) };
}

export function CobranzaPage() {
  const [preset, setPreset] = useState<RangoPreset>("hoy");
  const [customDesde, setCustomDesde] = useState<string>("");
  const [customHasta, setCustomHasta] = useState<string>("");

  const { desde, hasta } = useMemo(
    () => rangoFechas(preset, customDesde, customHasta),
    [preset, customDesde, customHasta],
  );

  const { data, isLoading } = useRecaudacion(desde, hasta);
  const { data: prox } = useProximosVencer();
  // rc90 — ciclos por revisar (pagos tardíos sin decisión sobre el corte)
  const { data: ciclosRev } = useCiclosRevision();

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlarmClock size={16} /> Cobranza del día
          </h2>
          <p className="text-xs text-muted-foreground max-md:hidden">
            Recaudación agrupada por caja y por administrativos. Los pagos
            anulados no cuentan.
          </p>
        </div>

        {/* Filtros de fecha — rc139 móvil: envuelven */}
        <div className="flex items-center gap-1.5 max-md:flex-wrap">
          <Calendar size={12} className="text-muted-foreground" />
          {(["hoy", "semana", "mes"] as RangoPreset[]).map((p) => (
            <button
              key={p}
              onClick={() => setPreset(p)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                preset === p ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted/30"
              }`}
            >
              {p === "hoy" ? "Hoy" : p === "semana" ? "Últimos 7d" : "Este mes"}
            </button>
          ))}
          <button
            onClick={() => setPreset("custom")}
            className={`rounded-full border px-3 py-1 text-xs transition ${
              preset === "custom" ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted/30"
            }`}
          >
            Custom
          </button>
          {preset === "custom" && (
            <>
              <Input
                type="date" value={customDesde}
                onChange={(e) => setCustomDesde(e.target.value)}
                className="w-auto text-xs"
              />
              <span className="text-xs text-muted-foreground">—</span>
              <Input
                type="date" value={customHasta}
                onChange={(e) => setCustomHasta(e.target.value)}
                className="w-auto text-xs"
              />
            </>
          )}
        </div>
      </div>

      {/* Totales */}
      {data && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <TotalCard label="Total cajas" value={data.total_cajas} accent="emerald" />
          <TotalCard label="Total admins" value={data.total_admins} accent="primary" />
          <TotalCard label="TOTAL GENERAL" value={data.total_general} bold />
          <TotalCard
            label="# pagos"
            value={String(data.num_pagos_total)}
            isCount
          />
        </div>
      )}

      {/* Recaudación cajas */}
      <Section
        title="Recaudación por caja"
        icon={<Wallet size={14} />}
        rows={data?.cajas ?? []}
        loading={isLoading}
        emptyText="No hay pagos de cajeros en este rango."
      />

      {/* Recaudación admins */}
      <Section
        title="Recaudación admins"
        icon={<Users size={14} />}
        rows={data?.admins ?? []}
        loading={isLoading}
        emptyText="No hay pagos de admins en este rango."
      />

      {/* rc90 — Ciclos por revisar (solo se muestra si hay pendientes) */}
      {(ciclosRev?.total ?? 0) > 0 && (
        <CiclosRevisionSection items={ciclosRev?.items ?? []} />
      )}

      {/* Próximos a vencer */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-500" />
            <h3 className="text-sm font-semibold">Contratos próximos a vencer</h3>
          </div>
          <span className="text-[10px] text-muted-foreground">
            Próximos {prox?.dias_anticipacion ?? 5} días · {prox?.total ?? 0} clientes
          </span>
        </div>
        {(!prox || prox.items.length === 0) ? (
          <div className="p-6">
            <EmptyState
              icon={AlertTriangle}
              title="Sin próximos a vencer"
              description="Ningún contrato activo vence en los próximos días."
            />
          </div>
        ) : isMobile ? (
          <MobileList className="p-3">
            {prox.items.map((c) => (
              <MobileCard
                key={c.factura_id}
                to={`/clientes/${c.cliente_id}`}
                title={c.cliente_nombre}
                subtitle={<><span className="font-mono">{c.cliente_codigo}</span> · {c.plan_nombre}</>}
                right={
                  <Badge variant={c.dias_para_vencer <= 1 ? "destructive" : "secondary"}>
                    {c.dias_para_vencer === 0 ? "Hoy" : `${c.dias_para_vencer}d`}
                  </Badge>
                }
              >
                <MobileMeta label="Factura" mono>{c.factura_folio ?? "—"}</MobileMeta>
                <MobileMeta label="Saldo" mono>{formatMXN(Number(c.saldo))}</MobileMeta>
                <MobileMeta label="Vence" span>{c.fecha_vencimiento}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Factura</TableHead>
                <TableHead>Vence</TableHead>
                <TableHead>Días</TableHead>
                <TableHead className="text-right pr-4">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prox.items.map((c) => (
                <TableRow key={c.factura_id}>
                  <TableCell>
                    <Link to={`/clientes/${c.cliente_id}`} className="text-primary hover:underline">
                      {c.cliente_nombre}
                    </Link>
                    <div className="text-[10px] text-muted-foreground font-mono">{c.cliente_codigo}</div>
                  </TableCell>
                  <TableCell>{c.plan_nombre}</TableCell>
                  <TableCell className="font-mono text-xs">{c.factura_folio ?? "—"}</TableCell>
                  <TableCell className="text-xs">{c.fecha_vencimiento}</TableCell>
                  <TableCell>
                    <Badge variant={c.dias_para_vencer <= 1 ? "destructive" : "secondary"}>
                      {c.dias_para_vencer === 0 ? "Hoy" : `${c.dias_para_vencer}d`}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right pr-4 font-mono">{formatMXN(Number(c.saldo))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ─── Sub-componentes ────────────────────────────────────────────────────

/** rc90 — Sección "Ciclos por revisar": contratos que pagaron tras suspensión
 * larga sin que nadie decidiera si re-anclar el corte (típicamente Toku).
 * Acciones solo pa' admins; el resto ve la lista informativa. */
function CiclosRevisionSection({ items }: { items: CicloRevisionItem[] }) {
  const user = useAuthStore((s) => s.user);
  const esAdmin = ["administrador", "superadmin"].includes(user?.rol?.nombre ?? "");
  const reanclarMut = useReanclarCiclo();
  const descartarMut = useDescartarRevisionCiclo();
  const [procesando, setProcesando] = useState<string | null>(null);

  async function resolver(item: CicloRevisionItem, actualizar: boolean) {
    setProcesando(item.contrato_id);
    try {
      if (actualizar) {
        await reanclarMut.mutateAsync({
          contratoId: item.contrato_id,
          dia: item.dia_sugerido,
          fechaPago: item.fecha_pago,
          motivo: `Pago tardío del ${item.fecha_pago} (revisión de ciclo en Cobranza)`,
        });
        toast.success(
          `${item.contrato_codigo}: corte actualizado al día ${item.dia_sugerido}`,
          { description: "La próxima mensualidad se emitirá para el mes siguiente." },
        );
      } else {
        await descartarMut.mutateAsync(item.contrato_id);
        toast.success(`${item.contrato_codigo}: se conservó el corte actual`);
      }
    } catch (err) {
      toast.error("No se pudo resolver la revisión", {
        description: (err as ApiError).message,
      });
    } finally {
      setProcesando(null);
    }
  }

  const isMobile = useIsMobile();

  return (
    <div className="rounded-lg border border-amber-300 bg-card dark:border-amber-900/40">
      <div className="flex items-center justify-between border-b border-amber-300/60 p-3 dark:border-amber-900/40 max-md:flex-col max-md:items-start max-md:gap-1">
        <div className="flex items-center gap-2">
          <CalendarClock size={14} className="text-amber-500" />
          <h3 className="text-sm font-semibold">Ciclos por revisar</h3>
        </div>
        <span className="text-[10px] text-muted-foreground">
          {items.length} contrato{items.length !== 1 ? "s" : ""} · pagaron tras
          suspensión larga sin decisión sobre el corte
        </span>
      </div>
      {isMobile ? (
        <MobileList className="p-3">
          {items.map((it) => (
            <MobileCard
              key={it.contrato_id}
              to={`/contratos/${it.contrato_id}`}
              title={it.cliente_nombre}
              subtitle={<><span className="font-mono">{it.cliente_codigo}</span> · <span className="font-mono">{it.contrato_codigo}</span></>}
              right={<Badge variant="secondary">día {it.fecha_corte_actual}</Badge>}
              actions={
                esAdmin ? (
                  <>
                    <Button size="sm" variant="outline" disabled={procesando === it.contrato_id} onClick={() => resolver(it, false)}>Conservar</Button>
                    <Button size="sm" disabled={procesando === it.contrato_id} onClick={() => resolver(it, true)} className="bg-amber-600 text-white hover:bg-amber-700">
                      <CheckCircle2 size={12} /> Corte al día {it.dia_sugerido}
                    </Button>
                  </>
                ) : (
                  <span className="text-[10px] text-muted-foreground">solo admins</span>
                )
              }
            >
              <MobileMeta label="Pagó" mono>{it.fecha_pago}</MobileMeta>
              <MobileMeta label="Última mensualidad" mono>{it.ultima_mensualidad_vencimiento ?? "—"}</MobileMeta>
            </MobileCard>
          ))}
        </MobileList>
      ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cliente</TableHead>
            <TableHead>Contrato</TableHead>
            <TableHead>Pagó</TableHead>
            <TableHead>Última mensualidad</TableHead>
            <TableHead>Corte actual</TableHead>
            <TableHead className="text-right pr-4">Acción</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.contrato_id}>
              <TableCell>
                <Link to={`/clientes/${it.cliente_id}`} className="text-primary hover:underline">
                  {it.cliente_nombre}
                </Link>
                <div className="text-[10px] text-muted-foreground font-mono">{it.cliente_codigo}</div>
              </TableCell>
              <TableCell>
                <Link to={`/contratos/${it.contrato_id}`} className="font-mono text-xs text-primary hover:underline">
                  {it.contrato_codigo}
                </Link>
              </TableCell>
              <TableCell className="font-mono text-xs">{it.fecha_pago}</TableCell>
              <TableCell className="font-mono text-xs">
                {it.ultima_mensualidad_vencimiento ?? "—"}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">día {it.fecha_corte_actual}</Badge>
              </TableCell>
              <TableCell className="text-right pr-4">
                {esAdmin ? (
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      size="sm" variant="outline"
                      disabled={procesando === it.contrato_id}
                      onClick={() => resolver(it, false)}
                    >
                      Conservar
                    </Button>
                    <Button
                      size="sm"
                      disabled={procesando === it.contrato_id}
                      onClick={() => resolver(it, true)}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <CheckCircle2 size={12} />
                      Corte al día {it.dia_sugerido}
                    </Button>
                  </div>
                ) : (
                  <span className="text-[10px] text-muted-foreground">solo admins</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
    </div>
  );
}

function TotalCard({
  label, value, accent, bold, isCount,
}: {
  label: string; value: string;
  accent?: "emerald" | "primary"; bold?: boolean; isCount?: boolean;
}) {
  const accentClass =
    accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" :
    accent === "primary" ? "text-primary" : "";
  return (
    <div className={`flex flex-col gap-1 rounded-lg border bg-card p-3 ${bold ? "border-primary/40 bg-primary/5" : ""}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${accentClass} ${bold ? "text-primary" : ""}`}>
        {isCount ? value : formatMXN(Number(value))}
      </div>
    </div>
  );
}

function Section({
  title, icon, rows, loading, emptyText,
}: {
  title: string;
  icon: React.ReactNode;
  rows: RecaudacionUserRow[];
  loading: boolean;
  emptyText: string;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <span className="text-[10px] text-muted-foreground">
          {rows.length} usuario{rows.length !== 1 ? "s" : ""}
        </span>
      </div>
      {loading && rows.length === 0 ? (
        <div className="p-4 text-center text-xs text-muted-foreground">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={Wallet} title="Sin movimiento" description={emptyText} />
        </div>
      ) : (
        <div className="divide-y">
          {rows.map((r) => (
            <UserRow key={r.user_id ?? r.user_email} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function UserRow({ row }: { row: RecaudacionUserRow }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/30"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <div>
            <div className="text-sm font-medium">{row.user_nombre}</div>
            <div className="text-[10px] text-muted-foreground">
              {row.user_email}
              {row.rol_nombre && (
                <Badge variant="outline" className="ml-1 text-[9px] capitalize">{row.rol_nombre}</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-muted-foreground">{row.num_pagos} pago{row.num_pagos !== 1 ? "s" : ""}</span>
          <span className="font-mono text-sm font-semibold">{formatMXN(Number(row.total))}</span>
        </div>
      </button>
      {open && row.pagos.length > 0 && isMobile && (
        <div className="border-t bg-muted/10 px-3 py-2">
          <MobileList>
            {row.pagos.map((p) => (
              <MobileCard
                key={p.pago_id}
                to={`/clientes/${p.cliente_id}`}
                title={p.cliente_nombre}
                subtitle={<>{formatDateTime(p.created_at)} · <span className="capitalize">{p.metodo}</span></>}
                right={<span className="font-mono text-sm font-semibold">{formatMXN(Number(p.monto))}</span>}
              >
                <MobileMeta label="Factura" mono>{p.factura_folio ?? "—"}</MobileMeta>
                <MobileMeta label="Ref.">{p.referencia ?? "—"}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        </div>
      )}
      {open && row.pagos.length > 0 && !isMobile && (
        <div className="border-t bg-muted/10 px-3 py-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px]">Hora</TableHead>
                <TableHead className="text-[10px]">Cliente</TableHead>
                <TableHead className="text-[10px]">Factura</TableHead>
                <TableHead className="text-[10px]">Método</TableHead>
                <TableHead className="text-[10px]">Ref.</TableHead>
                <TableHead className="text-[10px] text-right pr-4">Monto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {row.pagos.map((p) => (
                <TableRow key={p.pago_id}>
                  <TableCell className="text-[11px] text-muted-foreground">
                    {formatDateTime(p.created_at)}
                  </TableCell>
                  <TableCell>
                    <Link to={`/clientes/${p.cliente_id}`} className="text-primary hover:underline text-xs">
                      {p.cliente_nombre}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">{p.factura_folio ?? "—"}</TableCell>
                  <TableCell className="text-[11px] capitalize">{p.metodo}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">{p.referencia ?? "—"}</TableCell>
                  <TableCell className="text-right pr-4 font-mono text-xs">{formatMXN(Number(p.monto))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
